import forge from 'node-forge';
import { transceive } from './actions';
import { stringToHex } from './backupService';

/**
 * Certificate generation utilities for PIV
 * Generates self-signed X.509 certificates
 */

/**
 * Generate a self-signed X.509 certificate
 * @param {Object} options - Certificate options
 * @returns {Object} Certificate data
 */
export function generateSelfSignedCertificate(options) {
  const {
    publicKey,      // Public key in forge format
    subject,        // Subject DN object { CN, O, OU, C, ST, L }
    validityDays = 365,
    serialNumber = null
  } = options;

  try {
    // Create certificate
    const cert = forge.pki.createCertificate();

    // Set version (X.509 v3)
    cert.version = 2;

    // Set serial number
    cert.serialNumber = serialNumber || generateSerialNumber();

    // Set validity period
    cert.validity.notBefore = new Date();
    cert.validity.notAfter = new Date();
    cert.validity.notAfter.setDate(cert.validity.notAfter.getDate() + validityDays);

    // Set subject and issuer (same for self-signed)
    const attrs = buildDistinguishedName(subject);
    cert.subject.attributes = attrs;
    cert.issuer.attributes = attrs;

    // Set public key
    cert.publicKey = publicKey;

    // Add extensions
    cert.setExtensions([
      {
        name: 'basicConstraints',
        cA: false
      },
      {
        name: 'keyUsage',
        digitalSignature: true,
        keyEncipherment: true,
        dataEncipherment: true
      },
      {
        name: 'extKeyUsage',
        clientAuth: true,
        emailProtection: true
      },
      {
        name: 'subjectKeyIdentifier'
      }
    ]);

    // Self-sign the certificate
    // Note: We can't actually sign with the private key on the card here
    // This creates a certificate structure that will be signed by the card
    cert.sign(publicKey, forge.md.sha256.create());

    return {
      certificate: cert,
      pem: forge.pki.certificateToPem(cert),
      der: forge.asn1.toDer(forge.pki.certificateToAsn1(cert)).getBytes()
    };
  } catch (error) {
    console.error('Failed to generate certificate:', error);
    throw new Error('Certificate generation failed: ' + error.message);
  }
}

/**
 * Build Distinguished Name attributes
 * @param {Object} subject - Subject information
 * @returns {Array} Attributes array
 */
function buildDistinguishedName(subject) {
  const attrs = [];

  if (subject.CN) {
    attrs.push({ name: 'commonName', value: subject.CN });
  }
  if (subject.O) {
    attrs.push({ name: 'organizationName', value: subject.O });
  }
  if (subject.OU) {
    attrs.push({ name: 'organizationalUnitName', value: subject.OU });
  }
  if (subject.C) {
    attrs.push({ name: 'countryName', value: subject.C });
  }
  if (subject.ST) {
    attrs.push({ name: 'stateOrProvinceName', value: subject.ST });
  }
  if (subject.L) {
    attrs.push({ name: 'localityName', value: subject.L });
  }

  return attrs;
}

/**
 * Generate random serial number
 * @returns {string} Hex serial number
 */
function generateSerialNumber() {
  const bytes = forge.random.getBytesSync(16);
  return forge.util.bytesToHex(bytes);
}

/**
 * Extract public key from PIV slot
 * @param {Function} dispatch - Redux dispatch
 * @param {string} slot - PIV slot (9A, 9C, 9D, 9E)
 * @returns {Object} Public key in forge format
 */
export async function extractPublicKeyFromSlot(dispatch, slot) {
  try {
    // Select PIV applet
    await dispatch(transceive('00A4040009A00000030800001000'));

    // Generate a dummy signature to extract public key
    // This is a workaround - ideally we'd read the certificate
    // For now, we'll read the certificate and extract the public key

    const slotTags = {
      '9A': '5FC105',
      '9C': '5FC10A',
      '9D': '5FC10B',
      '9E': '5FC101'
    };

    const tag = slotTags[slot];
    if (!tag) {
      throw new Error('Invalid slot');
    }

    // Try to read existing certificate
    const lc = (tag.length / 2).toString(16).padStart(2, '0');
    const apdu = `00CB3FFF${lc}5C03${tag}`;
    let response = await dispatch(transceive(apdu));
    let sw = response.slice(-4);

    if (sw === '9000') {
      let certData = response.slice(0, -4);

      // Handle chunked response
      while (sw.startsWith('61')) {
        const remaining = parseInt(sw.slice(2), 16);
        const getResponse = `00C00000${remaining.toString(16).padStart(2, '0')}`;
        response = await dispatch(transceive(getResponse));
        sw = response.slice(-4);
        certData += response.slice(0, -4);
      }

      if (certData && certData.length > 0) {
        // Parse certificate and extract public key
        const certBytes = forge.util.hexToBytes(certData);
        const asn1 = forge.asn1.fromDer(certBytes);
        const cert = forge.pki.certificateFromAsn1(asn1);
        return cert.publicKey;
      }
    }

    throw new Error('No certificate found in slot. Generate a key first.');
  } catch (error) {
    console.error('Failed to extract public key:', error);
    throw error;
  }
}

/**
 * Generate public key from key components (for newly imported keys)
 * @param {Object} keyData - Key data from keyParser
 * @returns {Object} Public key in forge format
 */
export function generatePublicKeyFromComponents(keyData) {
  try {
    if (keyData.type === 'RSA') {
      // Create RSA public key from modulus and exponent
      const n = new forge.jsbn.BigInteger(keyData.n, 16);
      const e = new forge.jsbn.BigInteger(keyData.e, 16);

      const publicKey = forge.pki.rsa.setPublicKey(n, e);
      return publicKey;
    } else if (keyData.type === 'ECC') {
      // ECC public key generation is more complex
      // For now, we'll need to use the card to generate it
      throw new Error('ECC public key extraction not yet implemented. Generate key on card first.');
    }

    throw new Error('Unsupported key type');
  } catch (error) {
    console.error('Failed to generate public key:', error);
    throw error;
  }
}

/**
 * Import certificate to PIV slot
 * @param {Function} dispatch - Redux dispatch
 * @param {string} slot - PIV slot
 * @param {string} certDER - Certificate in DER format (hex string)
 * @param {string} pin - PIV PIN
 * @returns {boolean} Success
 */
export async function importCertificateToSlot(dispatch, slot, certDER, pin) {
  try {
    // Select PIV applet
    await dispatch(transceive('00A4040009A00000030800001000'));

    // Verify PIN
    const pinHex = stringToHex(pin);
    const verifyAPDU = `00200080${(pinHex.length / 2).toString(16).padStart(2, '0')}${pinHex}`;
    const verifyRes = await dispatch(transceive(verifyAPDU));

    if (!verifyRes.endsWith('9000')) {
      throw new Error('PIN verification failed');
    }

    // Build PUT DATA command
    const slotTags = {
      '9A': '5FC105',
      '9C': '5FC10A',
      '9D': '5FC10B',
      '9E': '5FC101'
    };

    const tag = slotTags[slot];
    if (!tag) {
      throw new Error('Invalid slot');
    }

    // Certificate must be wrapped in TLV: 53 [len] [cert]
    const certLen = (certDER.length / 2).toString(16).padStart(4, '0');
    const tlv = `53${certLen}${certDER}`;

    // Build APDU: PUT DATA
    const tlvLen = (tlv.length / 2).toString(16).padStart(2, '0');
    const apdu = `00DB3FFF${tlvLen}5C03${tag}${tlv}`;

    // Send in chunks if too large
    if (apdu.length > 510) {
      // Need to implement chaining for large certificates
      throw new Error('Certificate too large. Chaining not yet implemented.');
    }

    const response = await dispatch(transceive(apdu));

    if (response.endsWith('9000')) {
      return true;
    } else {
      throw new Error(`Import failed with status: ${response.slice(-4)}`);
    }
  } catch (error) {
    console.error('Failed to import certificate:', error);
    throw error;
  }
}

/**
 * Generate and import self-signed certificate
 * Complete workflow for PIV slot
 * @param {Function} dispatch - Redux dispatch
 * @param {string} slot - PIV slot
 * @param {Object} subject - Subject DN
 * @param {Object} keyData - Key data (if just imported)
 * @param {string} pin - PIV PIN
 * @param {number} validityDays - Certificate validity
 * @returns {Object} Result
 */
export async function generateAndImportCertificate(dispatch, slot, subject, keyData, pin, validityDays = 365) {
  try {
    // Step 1: Get or generate public key
    let publicKey;
    if (keyData) {
      // Key was just imported, generate public key from components
      publicKey = generatePublicKeyFromComponents(keyData);
    } else {
      // Key already exists, extract from slot
      publicKey = await extractPublicKeyFromSlot(dispatch, slot);
    }

    // Step 2: Generate certificate
    const certData = generateSelfSignedCertificate({
      publicKey: publicKey,
      subject: subject,
      validityDays: validityDays
    });

    // Step 3: Import certificate to slot
    const certDER = forge.util.bytesToHex(certData.der);
    await importCertificateToSlot(dispatch, slot, certDER, pin);

    return {
      success: true,
      certificate: certData.pem,
      subject: subject,
      validUntil: new Date(Date.now() + validityDays * 24 * 60 * 60 * 1000).toISOString()
    };
  } catch (error) {
    console.error('Failed to generate and import certificate:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Generate Certificate Signing Request (CSR)
 * @param {Object} publicKey - Public key
 * @param {Object} subject - Subject DN
 * @returns {string} CSR in PEM format
 */
export function generateCSR(publicKey, subject) {
  try {
    const csr = forge.pki.createCertificationRequest();

    // Set subject
    csr.subject.attributes = buildDistinguishedName(subject);

    // Set public key
    csr.publicKey = publicKey;

    // Add attributes
    csr.setAttributes([
      {
        name: 'challengePassword',
        value: ''
      },
      {
        name: 'extensionRequest',
        extensions: [
          {
            name: 'keyUsage',
            digitalSignature: true,
            keyEncipherment: true
          }
        ]
      }
    ]);

    // Sign CSR (self-signed for now)
    csr.sign(publicKey, forge.md.sha256.create());

    return forge.pki.certificationRequestToPem(csr);
  } catch (error) {
    console.error('Failed to generate CSR:', error);
    throw new Error('CSR generation failed: ' + error.message);
  }
}

/**
 * Parse certificate from DER/PEM
 * @param {string} certData - Certificate data
 * @returns {Object} Parsed certificate info
 */
export function parseCertificate(certData) {
  try {
    let cert;

    if (certData.includes('BEGIN CERTIFICATE')) {
      // PEM format
      cert = forge.pki.certificateFromPem(certData);
    } else {
      // DER format (hex string)
      const certBytes = forge.util.hexToBytes(certData);
      const asn1 = forge.asn1.fromDer(certBytes);
      cert = forge.pki.certificateFromAsn1(asn1);
    }

    return {
      subject: cert.subject.attributes.map(attr => `${attr.shortName}=${attr.value}`).join(', '),
      issuer: cert.issuer.attributes.map(attr => `${attr.shortName}=${attr.value}`).join(', '),
      serialNumber: cert.serialNumber,
      notBefore: cert.validity.notBefore,
      notAfter: cert.validity.notAfter,
      publicKey: {
        algorithm: cert.publicKey.n ? 'RSA' : 'ECC',
        size: cert.publicKey.n ? cert.publicKey.n.bitLength() : 256
      }
    };
  } catch (error) {
    console.error('Failed to parse certificate:', error);
    throw new Error('Certificate parsing failed: ' + error.message);
  }
}

/**
 * Validate certificate subject DN
 * @param {Object} subject - Subject DN object
 * @returns {Object} Validation result
 */
export function validateSubjectDN(subject) {
  if (!subject.CN || subject.CN.trim().length === 0) {
    return {
      valid: false,
      error: 'Common Name (CN) is required'
    };
  }

  if (subject.CN.length > 64) {
    return {
      valid: false,
      error: 'Common Name is too long (max 64 characters)'
    };
  }

  if (subject.C && subject.C.length !== 2) {
    return {
      valid: false,
      error: 'Country code must be 2 characters (e.g., US, CN)'
    };
  }

  return { valid: true };
}
